import { useEffect, useState } from "react";
import { Users, Bell, FileSpreadsheet, Trophy, Send } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—");
const STATE_CLS = { Riscattato: "text-emerald-400", Scaduto: "text-muted-foreground", "Da usare": "text-amber-400" };

function Kpi({ label, value, testId }) {
  return (
    <div className="rounded-lg bg-secondary/40 p-3" data-testid={testId}>
      <div className="text-[11px] text-muted-foreground">{label}</div>
      <div className="font-display font-bold text-xl">{value}</div>
    </div>
  );
}

function RankList({ title, month, top, onSend }) {
  return (
    <div className="rounded-lg bg-secondary/30 p-3" data-testid={`ref-rank-${month}`}>
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-xs font-semibold text-muted-foreground flex items-center gap-1"><Trophy className="h-3.5 w-3.5" /> {title} <span className="font-mono-code">{month}</span></div>
        {onSend && <Button size="sm" variant="ghost" className="h-6 text-[11px] gap-1" onClick={onSend} data-testid={`ref-rank-send-${month}`}><Send className="h-3 w-3" /> Invia ora</Button>}
      </div>
      {top.length === 0 ? <p className="text-xs text-muted-foreground">Nessun invito premiato</p> : (
        <ol className="space-y-1 text-xs">
          {top.map((a) => <li key={a.customer_id} className="flex justify-between gap-2"><span><b className="font-mono-code mr-1.5">{a.position}°</b>{a.email}</span><b>{a.friends} {a.friends === 1 ? "amico" : "amici"}</b></li>)}
        </ol>
      )}
    </div>
  );
}

export function ReferralsPanel({ contests = [] }) {
  const [d, setD] = useState(null);
  const [cid, setCid] = useState("");
  useEffect(() => { api.referralsAdmin(cid).then(setD).catch(() => {}); }, [cid]);
  const remind = async () => {
    try { const r = await api.sendReferralReminders(); toast.success(`Promemoria premi invito: ${r.sent} inviati${r.failed ? `, ${r.failed} falliti` : ""}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Invio non riuscito"); }
  };
  const sendRank = async (month) => {
    try { const r = await api.sendReferralRank(month); toast.success(`Classifica invitanti ${r.month}: ${r.sent.length} comunicazioni inviate`); api.referralsAdmin(cid).then(setD); }
    catch (e) { toast.error(e?.response?.data?.detail || "Invio non riuscito"); }
  };
  if (!d) return null;
  return (
    <Card className="p-5 bg-card border-border space-y-3" data-testid="referrals-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-display font-bold text-lg flex items-center gap-2"><Users className="h-4 w-4" /> Premi invito</h3>
        <div className="flex items-center gap-1.5">
          <select value={cid} onChange={(e) => setCid(e.target.value)} className="h-7 text-xs rounded-md border border-border bg-background px-2" data-testid="ref-contest-filter">
            <option value="">Tutti i concorsi</option>
            {contests.map((c) => <option key={c.id} value={c.id}>{c.label || c.name}</option>)}
          </select>
          <Button size="sm" variant="ghost" className="h-7 text-xs gap-1" onClick={remind} data-testid="ref-remind"><Bell className="h-3.5 w-3.5" /> Promemoria ora</Button>
          <a href={api.referralsExportUrl(cid)} className="inline-flex items-center gap-1 h-7 px-2 rounded-md text-xs border border-border hover:bg-secondary/60" data-testid="ref-export"><FileSpreadsheet className="h-3.5 w-3.5" /> Excel</a>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Kpi label="Emessi" value={d.issued} testId="ref-issued" />
        <Kpi label="Riscattati" value={d.redeemed} testId="ref-redeemed" />
        <Kpi label="Scaduti" value={d.expired} testId="ref-expired" />
        <Kpi label="Tasso riscatto" value={`${d.rate}%`} testId="ref-rate" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <RankList title="Classifica invitanti" month={d.rank.current.month} top={d.rank.current.top} />
        <RankList title="Mese precedente" month={d.rank.previous.month} top={d.rank.previous.top} onSend={() => sendRank(d.rank.previous.month)} />
      </div>
      {d.rows.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="ref-empty">Nessun premio invito emesso: attiva "Premio invito" nella tab Landing del concorso.</p>
      ) : (
        <div className="overflow-auto max-h-80">
          <table className="w-full text-xs" data-testid="ref-table">
            <thead className="text-left text-muted-foreground sticky top-0 bg-card"><tr><th className="py-1 pr-2">Data</th><th className="py-1 pr-2">Concorso</th><th className="py-1 pr-2">Invitante</th><th className="py-1 pr-2">Amico</th><th className="py-1 pr-2">Codice</th><th className="py-1 pr-2">Stato</th><th className="py-1 pr-2">Sede</th><th className="py-1">Promemoria</th></tr></thead>
            <tbody>
              {d.rows.map((r) => (
                <tr key={r.id} className="border-t border-border/50" data-testid={`ref-row-${r.id}`}>
                  <td className="py-1.5 pr-2 font-mono-code text-muted-foreground whitespace-nowrap">{fmt(r.created_at)}</td>
                  <td className="py-1.5 pr-2 truncate max-w-[140px]">{r.contest}</td>
                  <td className="py-1.5 pr-2">{r.referrer}</td>
                  <td className="py-1.5 pr-2">{r.friend}</td>
                  <td className="py-1.5 pr-2 font-mono-code">{r.code}</td>
                  <td className={`py-1.5 pr-2 font-semibold ${STATE_CLS[r.state] || ""}`}>{r.state}{r.redeemed_at ? <span className="block font-normal text-muted-foreground">{fmt(r.redeemed_at)}</span> : null}</td>
                  <td className="py-1.5 pr-2">{r.store || "—"}</td>
                  <td className="py-1.5 font-mono-code text-muted-foreground">{fmt(r.reminder_sent_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
